import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Newspaper,
  Gift,
  CalendarDays,
  Tag,
  Images,
  Disc,
  Camera,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',   to: '/dashboard',   icon: <LayoutDashboard size={18} /> },
  { label: 'Members',     to: '/members',     icon: <Users size={18} /> },
  { label: 'News',        to: '/news',        icon: <Newspaper size={18} /> },
  { label: 'Schedule',       to: '/schedule',       icon: <CalendarDays size={18} /> },
  { label: 'Schedule Types', to: '/schedule-types', icon: <Tag size={18} /> },
  { label: 'Cheki Orders',   to: '/cheki-orders',   icon: <Camera size={18} /> },
  { label: 'Discography',    to: '/discography',    icon: <Disc size={18} /> },
  { label: 'Benefits',    to: '/benefits',    icon: <Gift size={18} /> },
  { label: 'Assets',      to: '/assets',      icon: <Images size={18} /> },
];

function SidebarNav({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-5 py-4">
        <span className="text-xl font-bold tracking-tight" style={{ color: 'var(--tsuyosa-pink)' }}>
          Tsuyosa Admin
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-pink-500/10 text-pink-400'
                      : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="border-t border-[var(--color-border)] p-4">
        <div className="mb-3 flex items-center gap-2 px-1">
          <div className="h-7 w-7 rounded-full bg-pink-500/20 flex items-center justify-center text-xs font-semibold text-pink-400">
            {user?.email?.[0]?.toUpperCase() ?? 'A'}
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-gray-300">{user?.email}</p>
            <p className="text-[10px] text-gray-500 capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-white/5 hover:text-red-400 transition-colors"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-background)]">
      <aside className="hidden w-56 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-surface)] lg:flex lg:flex-col">
        <SidebarNav />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="relative z-10 flex h-full w-56 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]">
            <button
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-200"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={20} />
            </button>
            <SidebarNav onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center gap-3 border-b border-[var(--color-border)] px-4 lg:px-6">
          <button
            className="text-gray-400 hover:text-gray-200 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          <h1 className="text-sm font-medium text-gray-300">Tsuyosa</h1>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
